import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VerificationForm = ({ formData, errors, onChange, verificationStatus }) => {
  const [dragActive, setDragActive] = useState(false);

  const documentTypes = [
    { id: 'registration', label: 'Business Registration Certificate', icon: 'FileText', required: true },
    { id: 'tax', label: 'Tax Identification Document', icon: 'FileSpreadsheet', required: true },
    { id: 'authorization', label: 'Authorization Letter', icon: 'Award', required: false }
  ];

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e, type) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);

    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFileUpload(e?.dataTransfer?.files?.[0], type);
    }
  };

  const handleFileUpload = (file, type) => {
    const mockDocument = {
      id: Date.now(),
      type,
      name: file?.name,
      size: file?.size,
      uploadDate: new Date()?.toISOString(),
      status: 'uploaded'
    };
    const updatedDocs = [...(formData?.documents || []), mockDocument];
    onChange('documents', updatedDocs);
  };

  const handleDeleteDocument = (docId) => {
    const updatedDocs = formData?.documents?.filter(d => d?.id !== docId);
    onChange('documents', updatedDocs);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const getVerificationStatusInfo = () => {
    const statuses = {
      pending: { color: 'bg-warning/10 border-warning/20', text: 'text-warning', message: 'Verification pending. Please upload required documents and submit.' },
      under_review: { color: 'bg-accent/10 border-accent/20', text: 'text-accent', message: 'Your documents are under review. We will notify you within 2-3 business days.' },
      approved: { color: 'bg-success/10 border-success/20', text: 'text-success', message: 'Your company has been verified! You can now publish your profile.' },
      rejected: { color: 'bg-error/10 border-error/20', text: 'text-error', message: 'Verification failed. Please review feedback and resubmit documents.' }
    };
    return statuses?.[verificationStatus] || statuses?.pending;
  };

  const statusInfo = getVerificationStatusInfo();

  return (
    <div className="space-y-6">
      <div className={`p-4 rounded-lg border ${statusInfo?.color}`}>
        <p className={`text-sm font-medium ${statusInfo?.text}`}>{statusInfo?.message}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Business Registration Number"
          type="text"
          placeholder="Enter registration number"
          value={formData?.registrationNumber}
          onChange={(e) => onChange('registrationNumber', e?.target?.value)}
          error={errors?.registrationNumber}
          required
        />
        <Input
          label="Tax Identification Number"
          type="text"
          placeholder="Enter tax ID"
          value={formData?.taxId}
          onChange={(e) => onChange('taxId', e?.target?.value)}
          error={errors?.taxId}
          required
        />
      </div>

      <div>
        <h3 className="text-lg font-semibold text-card-foreground mb-4">Verification Documents</h3>
        <div className="grid grid-cols-1 gap-4">
          {documentTypes?.map((docType) => {
            const uploadedDocs = formData?.documents?.filter((d) => d?.type === docType?.id);

            return (
              <div key={docType?.id} className="bg-muted/50 rounded-lg border border-border p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name={docType?.icon} size={20} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-card-foreground">
                      {docType?.label}
                      {docType?.required && <span className="text-destructive ml-1">*</span>}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {uploadedDocs?.length} file{uploadedDocs?.length !== 1 ? 's' : ''} uploaded
                    </p>
                  </div>
                </div>
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-smooth ${
                    dragActive ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={(e) => handleDrop(e, docType?.id)}
                >
                  <Icon name="Upload" size={24} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                  <p className="text-sm text-card-foreground mb-1">Drag & drop or click to upload</p>
                  <p className="text-xs text-muted-foreground mb-3">PDF, JPG, PNG (Max 5MB)</p>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => e?.target?.files?.[0] && handleFileUpload(e?.target?.files?.[0], docType?.id)}
                    className="hidden"
                    id={`file-${docType?.id}`}
                  />
                  <label htmlFor={`file-${docType?.id}`}>
                    <Button variant="outline" size="sm" asChild>
                      <span>Choose File</span>
                    </Button>
                  </label>
                </div>
                {uploadedDocs?.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {uploadedDocs?.map((doc) => (
                      <div
                        key={doc?.id}
                        className="flex items-center justify-between p-2 bg-background rounded border border-border"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Icon name="File" size={16} color="var(--color-muted-foreground)" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-card-foreground truncate">{doc?.name}</p>
                            <p className="text-xs text-muted-foreground">{formatFileSize(doc?.size)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteDocument(doc?.id)}
                          className="text-error hover:text-error/80 transition-smooth ml-2"
                          aria-label="Delete document"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {errors?.documents && (
          <p className="text-sm text-destructive mt-2">{errors?.documents}</p>
        )}
      </div>
    </div>
  );
};

export default VerificationForm;